import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo } from 'react';
import { DndContext, DragOverlay, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors, useDroppable, } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Settings, Plus } from 'lucide-react';
import { KanbanWidgetSettings } from './KanbanWidgetSettings';
import { KanbanItemEditModal } from './KanbanItemEditModal';
// Draggable Card Component
function KanbanCard({ item, columns, visibleColumns, showColumnNames, displayCoverImage, isDark = true, onCardClick, people = [], }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, } = useSortable({ id: item.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (_jsxs("div", { ref: setNodeRef, style: style, ...attributes, ...listeners, onClick: () => !isDragging && onCardClick?.(item), className: `p-3 rounded-lg cursor-move mb-2 transition-colors ${isDark
            ? 'bg-gray-700 hover:bg-gray-600'
            : 'bg-white border border-gray-200 hover:border-gray-300'}`, children: [displayCoverImage && item.data['cover'] && (_jsx("div", { className: `mb-2 h-24 rounded overflow-hidden ${isDark ? 'bg-gray-600' : 'bg-gray-100'}`, children: _jsx("img", { src: item.data['cover'], alt: "Cover", className: "w-full h-full object-cover" }) })), _jsx("h4", { className: `text-sm font-medium mb-2 truncate ${isDark ? 'text-white' : 'text-gray-900'}`, children: item.data['col-1'] || 'Untitled' }), _jsx("div", { className: "space-y-1", children: visibleColumns.map(columnId => {
                    const column = columns.find(c => c.id === columnId);
                    if (!column || columnId === 'col-1')
                        return null;
                    const value = item.data[columnId];
                    if (!value && value !== 0)
                        return null;
                    // Handle person columns - display person name instead of UUID
                    let displayValue = String(value).substring(0, 50);
                    if ((column.type === 'person' || column.type === 'assignee') && value) {
                        const person = people.find(p => p.id === value || p.name === value);
                        if (person) {
                            displayValue = person.name;
                        }
                    }
                    return (_jsxs("div", { className: `text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`, children: [showColumnNames && (_jsxs("span", { className: isDark ? 'text-gray-400' : 'text-gray-500', children: [column.name, ": "] })), _jsx("span", { className: isDark ? 'text-white' : 'text-gray-900', children: displayValue })] }, columnId));
                }) })] }));
}
// Column Header Component
function ColumnHeader({ group, itemCount, hideIfEmpty, isDark = true, }) {
    if (hideIfEmpty && itemCount === 0)
        return null;
    return (_jsx("div", { className: "p-4 rounded-t-lg font-semibold text-white", style: { backgroundColor: group.color || '#6B7280' }, children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { className: "flex items-center gap-2", children: _jsx("h3", { children: group.name }) }), _jsx("span", { className: "text-sm", children: itemCount })] }) }));
}
// Kanban Column Component
function KanbanColumn({ group, items, columns, config, hideIfEmpty, isDark = true, onCardClick, onItemCreate, people = [], }) {
    const { setNodeRef, isOver } = useDroppable({
        id: group.id,
    });
    if (hideIfEmpty && items.length === 0)
        return null;
    return (_jsxs("div", { ref: setNodeRef, className: `flex-shrink-0 w-80 flex flex-col rounded-lg overflow-hidden transition-colors ${isOver
            ? 'bg-blue-500/10 ring-2 ring-blue-500'
            : isDark ? 'bg-gray-800' : 'bg-white border border-gray-200'}`, children: [_jsx(ColumnHeader, { group: group, itemCount: items.length, hideIfEmpty: hideIfEmpty, isDark: isDark }), _jsxs("div", { className: `flex-1 p-4 overflow-y-auto flex flex-col ${isDark ? 'bg-gray-800' : 'bg-white'}`, children: [_jsx("div", { className: "space-y-2", children: _jsx(SortableContext, { items: items.map(i => i.id), strategy: verticalListSortingStrategy, children: items.map(item => (_jsx(KanbanCard, { item: item, columns: columns, visibleColumns: config.visibleCardColumns, showColumnNames: config.showColumnNames, displayCoverImage: config.displayCoverImage, isDark: isDark, onCardClick: onCardClick, people: people }, item.id))) }) }), _jsxs("button", { onClick: () => onItemCreate?.(group.id), className: `mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium ${isDark
                            ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`, children: [_jsx(Plus, { className: "w-4 h-4" }), "Add item"] })] })] }));
}
export function EnhancedKanbanView({ items, groups, columns, onItemUpdate, onItemCreate, isDark = false, people = [], }) {
    const [showSettings, setShowSettings] = useState(false);
    const [activeItem, setActiveItem] = useState(null);
    const [selectedItemForEdit, setSelectedItemForEdit] = useState(null);
    // Default config
    const defaultConfig = {
        groupByColumn: 'col-2', // Default to status column
        divideByColumn: null,
        hideEmptyGroups: false,
        showColumnNames: true,
        displayCoverImage: false,
        visibleCardColumns: ['col-2', 'col-3', 'col-4', 'col-5'], // Default visible columns
        cardColumnOrder: ['col-2', 'col-3', 'col-4', 'col-5'],
    };
    const [config, setConfig] = useState(defaultConfig);
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 10,
        },
    }), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    // Get unique values from grouping column
    // If grouping by a status column, use the predefined options instead of scanning items
    const groupingValues = useMemo(() => {
        if (!config.groupByColumn)
            return [];
        // Check if the grouping column is a status column with predefined options
        const groupingColumn = columns.find(col => col.id === config.groupByColumn);
        if (groupingColumn && groupingColumn.type === 'status' && groupingColumn.options) {
            // Return the IDs of the status options in order
            return groupingColumn.options.map(opt => opt.id);
        }
        // Otherwise, get unique values from items
        const values = new Set();
        let hasEmptyValues = false;
        items.forEach(item => {
            const value = item.data[config.groupByColumn];
            if (value) {
                values.add(String(value));
            }
            else {
                hasEmptyValues = true;
            }
        });
        const result = Array.from(values).sort();
        // Add a special "empty" marker if there are items with no value
        if (hasEmptyValues) {
            result.push('__EMPTY__');
        }
        return result;
    }, [items, config.groupByColumn, columns]);
    // Organize items by grouping column
    const itemsByGrouping = useMemo(() => {
        if (!config.groupByColumn)
            return {};
        const organized = {};
        groupingValues.forEach(value => {
            organized[value] = [];
        });
        items.forEach(item => {
            const value = item.data[config.groupByColumn];
            if (value) {
                const key = String(value);
                if (!organized[key])
                    organized[key] = [];
                organized[key].push(item);
            }
            else {
                // Items with empty values go to the __EMPTY__ bucket
                if (organized['__EMPTY__']) {
                    organized['__EMPTY__'].push(item);
                }
            }
        });
        return organized;
    }, [items, config.groupByColumn, groupingValues]);
    const handleDragStart = (event) => {
        const item = items.find(i => i.id === event.active.id);
        setActiveItem(item || null);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveItem(null);
        if (!over)
            return;
        const draggedItem = items.find(i => i.id === active.id);
        if (!draggedItem)
            return;
        // If dropped on a group column, update the grouping value
        if (config.groupByColumn && groupingValues.includes(over.id)) {
            onItemUpdate(draggedItem.id, draggedItem.groupId, {
                [config.groupByColumn]: over.id,
            });
        }
    };
    return (_jsxs("div", { className: `h-full flex flex-col ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`, children: [_jsxs("div", { className: `flex items-center justify-between p-4 border-b ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`, children: [_jsx("h2", { className: `text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`, children: "Kanban View" }), _jsx("button", { onClick: () => setShowSettings(true), className: `p-2 rounded transition-colors ${isDark
                            ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                            : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'}`, title: "Kanban settings", children: _jsx(Settings, { className: "w-5 h-5" }) })] }), _jsx("div", { className: `flex-1 overflow-x-auto p-4 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`, children: _jsxs(DndContext, { sensors: sensors, collisionDetection: closestCorners, onDragStart: handleDragStart, onDragEnd: handleDragEnd, children: [_jsx("div", { className: "flex gap-4", children: config.groupByColumn && groupingValues.map(value => {
                                const columnItems = itemsByGrouping[value] || [];
                                // Get the group name and color from the column configuration if it's a status column
                                const groupingColumn = columns.find(col => col.id === config.groupByColumn);
                                let groupName = value;
                                let groupColor = '#6B7280';
                                // Handle special __EMPTY__ case for items with no value
                                if (value === '__EMPTY__') {
                                    groupName = `No ${groupingColumn?.name || 'Value'}`;
                                    groupColor = '#9CA3AF'; // Gray color for empty
                                }
                                else if (groupingColumn && groupingColumn.type === 'status' && groupingColumn.options) {
                                    const option = groupingColumn.options.find(opt => opt.id === value);
                                    if (option) {
                                        groupName = option.label;
                                        groupColor = option.color;
                                    }
                                }
                                return (_jsx(KanbanColumn, { group: {
                                        id: value,
                                        name: groupName,
                                        color: groupColor,
                                        position: 0,
                                        collapsed: false,
                                    }, items: columnItems, columns: columns, config: config, hideIfEmpty: config.hideEmptyGroups, isDark: isDark, onCardClick: setSelectedItemForEdit, onItemCreate: onItemCreate, people: people }, value));
                            }) }), _jsx(DragOverlay, { children: activeItem ? (_jsx("div", { className: "rotate-3 scale-105", children: _jsx(KanbanCard, { item: activeItem, columns: columns, visibleColumns: config.visibleCardColumns, showColumnNames: config.showColumnNames, displayCoverImage: config.displayCoverImage, isDark: isDark, people: people }) })) : null })] }) }), _jsx(KanbanWidgetSettings, { config: config, onConfigChange: setConfig, availableColumns: columns, isOpen: showSettings, onClose: () => setShowSettings(false) }), _jsx(KanbanItemEditModal, { isOpen: !!selectedItemForEdit, onClose: () => setSelectedItemForEdit(null), item: selectedItemForEdit || undefined, columns: columns, onItemUpdate: (columnId, value) => {
                    if (selectedItemForEdit) {
                        onItemUpdate(selectedItemForEdit.id, selectedItemForEdit.groupId, {
                            [columnId]: value,
                        });
                    }
                }, isDark: isDark })] }));
}
